import React, { useCallback, useMemo } from 'react';
import { Geometry, points } from '@turf/helpers';
import { Position } from 'deck.gl';
import bbox from '@turf/bbox';
import bboxToPolygon from '@turf/bbox-polygon';
import scale from '@turf/transform-scale';
import { BoundingBox } from './BoundingBox';
import { Polygon } from 'types/geojson';
import { hexToRgbArray } from 'kineticUtils/maps/hexToRgbArray';
import MapToolbox from 'components/maps/MapToolbox';
import { BaseMap, MapEntityClickEvent } from 'components/maps/BaseMap';

export const PolygonGeometryMap: React.FunctionComponent<{
  geometry?: Polygon;
  onChange?: (geometry: Polygon) => void;
  polygonColor?: string;
  polygonOpacity?: number;
  viewOnly?: boolean;
}> = ({ geometry, onChange, polygonColor, polygonOpacity, viewOnly = false }) => {
  const rgbaColor = useMemo(() => {
    return polygonColor ? hexToRgbArray(polygonColor) : undefined;
  }, [polygonColor]);

  // Get the bounding box of all points in the polygon to establish zoom level
  const mapBounds = useMemo(() => {
    if (!geometry) {
      return undefined;
    }
    // HACK: turf doesn't like the coordinates that are derived from a polygon
    const featureCollection = points(geometry?.coordinates[0] as unknown as Position[]);
    const scaled = featureCollection?.features?.length
      ? scale(bboxToPolygon(bbox(featureCollection)), 2)
      : null;
    return scaled ? bbox(scaled) : undefined;
  }, [geometry]);

  const handleLocationSelection = useCallback(
    (geometry: Geometry) => {
      onChange?.(geometry as Polygon);
    },
    [onChange]
  );

  const toolbox = useMemo(
    () => (
      <MapToolbox
        geometry={geometry as Geometry}
        verticalPosition='top'
        horizontalPosition='right'
        onLocationSelection={handleLocationSelection}
        verticalPadding={60}
        supportsPoint={false}
        supportsLine={false}
        supportsArea={!viewOnly}
        geoJsonStyles={
          rgbaColor && polygonOpacity
            ? {
                fillColor: [...rgbaColor, Math.ceil(255 * polygonOpacity)],
                lineColor: [...rgbaColor, 255],
                lineWidth: 15,
              }
            : { lineWidth: 15 }
        }
      />
    ),
    [geometry, handleLocationSelection, polygonOpacity, rgbaColor, viewOnly]
  );

  return (
    <BaseMap
      contextChildren={[toolbox]}
      showMapMarkers
      mapEntityClickEvents={[MapEntityClickEvent.RIGHT]}
      disableClusterSelection={true}
    >
      <BoundingBox mapBounds={mapBounds} />
    </BaseMap>
  );
};
